import axios from "axios";
import Config from "../../config";
import {retailerCatalogSearch, supplierCatalogSearch} from "../containers/catalog/catalog";
import {useEffect, useRef, useState} from "react";
import Auth from "../../auth";
import moment from "moment";
import FileSaver from "filesaver.js-npm";
import Helper, {validateFlatFile} from "../../helpers";
import cloneDeep from 'lodash/cloneDeep';
import {Properties} from "../../inventory/feeds";

export const categoryFields = ["Id", "ParentId", "Name", "AttributeSet", "DestinationId", "BannerURL", "SyncStatus", "Level", "Children", "Path", "LastModified", "Hidden", "HiddenFromPartner", "Position", "CustomAttributes"];
export const attributeSetFields = ["Name", "FriendlyName", "Attributes", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];
export const attributeFields = ["Name", "FriendlyName", "Type", "Description", "Options", "Group", "Tags", "DestinationId", "SyncStatus", "System", "CustomAttributes", "HiddenFromPartner", "Hidden", "Editor", "ValidationRules", "LastModified"];
export const approvalStatusOptions = ["All", "New", "Approved", "Ignored", "Flagged", "Update Suggested", "Not Compliant", "Under Review"];
export const listingStatusOptions = ["All", "Listing Failed", "Not Listed", "Delist Pending", "Listing Pending", "Listed", "Delisted"];
export const messageStatusOptions = ["All", "New", "Complete", "In Progress", "Blocked"];

//region Feature Toggles
export const SUPPLIER_SYNDICATION_ENABLED = !Config.production;
//endregion

//region Helper Functions
export function isNullOrEmpty(value) {
    return value === null || value === undefined || value === "";
}

export function unescape(string) {
    return new DOMParser().parseFromString(string, 'text/html').querySelector('html').textContent;
}

export const getTickValues = (allTicks, ticks) => {
    var ret = [];
    if (!allTicks || ticks <= 0) {
        return ret;
    }
    var spacing = Math.max(Math.ceil(allTicks.length / ticks), 1);
    for (var i = 0; i < allTicks.length; i += spacing) {
        ret.push(allTicks[i]);
    }
    if (ret.indexOf(allTicks[allTicks.length - 1]) < 0) {
        ret.push(allTicks[allTicks.length - 1]);
    }
    return ret;
}

export const escapeSpaces = (field, headers) => {
    if (field && headers.indexOf(field) > -1 && field.indexOf(" ") > -1) {
        return '{' + field + '}';
    }
    return field;
}

export function updateImportLinkTarget(link) {
    let csvId = link.split("/").pop();
    let updatedLink = "";

    if (link.includes("https://stage.commerceapi.io/api/v1/attachments/catalogmanagerfiles/")) {
        updatedLink = `https://stageportal.logicbroker.com/storage/catalogmanagerfiles/${csvId}`
    } else if (link.includes("https://commerceapi.io/api/v1/attachments/catalogmanagerfiles/")) {
        updatedLink = `https://portal.logicbroker.com/storage/catalogmanagerfiles/${csvId}`
    }

    return updatedLink;
}

export function rtrim(str, chr) {
    if (!chr) {
        return str.replace(/\s+$/, '');
    }
    // Escape special regex characters
    const escaped = chr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rgxtrim = new RegExp(escaped + '+$');
    return str.replace(rgxtrim, '');
}

const getIsSmallScreen = () => window.innerWidth <= 1380;
export default function useIsSmallScreen() {
    const [isSmall, setIsSmall] = useState(getIsSmallScreen());

    useEffect(() => {
        const onResize = () => {
            setIsSmall(getIsSmallScreen());
        }

        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        }
    }, []);

    return isSmall;
}

export function useDidUpdateEffect(fn, inputs) {
    const didMountRef = useRef(false);

    useEffect(() => {
        if (didMountRef.current) {
            return fn();
        }
        didMountRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, inputs);
}

export const approvalStatusColorMapper = (status) => {
    switch (status) {
        case "New":
            return "#275e7A"
        case "Approved":
            return "green";
        case "Ignored":
            return "grey";
        case "Flagged":
            return "red";
        case "Update Suggested":
            return "orange";
        case "Not Compliant":
            return "red";
        case "Under Review":
            return "orange";
        default:
            return "orange";
    }
}
export const listingStatusColorMapper = (status) => {
    switch (status) {
        case "Error":
            return "red"
        case "Not Listed":
            return "#275e7A";
        case "Delist Pending":
            return "orange";
        case "Listing Pending":
            return "orange";
        case "Listed":
            return "green";
        case "Delisted":
            return "grey";
        case "Listing Failed":
            return "red";
        default:
            return "orange";
    }
}
export const messageStatusColorMapper = (status) => {
    switch (status) {
        case "New":
            return "rgb(39, 94, 122)";
        case "Complete":
            return "rgb(68, 128, 68)";
        case "In Progress":
            return "rgb(255, 165, 0)";
        case "Blocked":
            return "rgb(238, 50, 36)";
        default:
            return "grey";
    }

}

export const SyncStatusColorMapper = (status) => {
    switch (status) {
        case "Synced":
            return "green"
        case "Not Synced":
            return "grey";
        default:
            return "grey";
    }
}
//endregion

//region Helper Components
export const useDebouncedEffect = (effect, deps, delay) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
}
//endregion

//region SupplierCatalog API Calls
export const approveProduct = async (productId, supplierId, supplierName, desiredSyncStatus, forceUpdate, merchantSku, merchantParentSku) => {
    if (!Auth.hasPermission("inventory/manage")) {
        return {
            success: false,
            data: null,
            error: "You do not have permission to approve products."
        }
    }

    let encodedProductId = encodeURIComponent(productId);
    let encodedMerchantSku = encodeURIComponent(merchantSku || productId);
    try {
        const listStatus = desiredSyncStatus || "Listing Pending";
        let productResponse = await axios.get(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`);
        let productToApprove = productResponse.data;

        productToApprove.Attributes.approval_status = "Approved";
        productToApprove.Attributes.approved_by = Auth.getUsername() || "";
        productToApprove.Attributes.approval_date = moment.utc().toISOString(true);
        productToApprove.Attributes.supplier_id = supplierId;

        productToApprove.Attributes.sync_status = listStatus;
        if (listStatus === "Delist Pending") {
            productToApprove.Attributes.delisted_date = moment.utc().toISOString(true);
            productToApprove.Attributes.delisted_by = Auth.getUsername();
        } else if (listStatus === "Listing Pending") {
            productToApprove.Attributes.listed_date = moment.utc().toISOString(true);
            productToApprove.Attributes.listed_by = Auth.getUsername();
        }

        try {
            await axios.get(Config.api + `/api/v2/ProductOnboardingCenter/Products?id=${encodedMerchantSku}`);
            const newRetailerProduct = cloneDeep(productToApprove);
            if (merchantSku) newRetailerProduct.Attributes.sku = merchantSku;
            if (merchantParentSku) newRetailerProduct.Attributes.parent_sku = merchantParentSku;
            if (forceUpdate) {
                await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/Products?id=${encodedMerchantSku}`, newRetailerProduct);
            } else {
                return {
                    success: false,
                    data: null,
                    error: "Product already exists in the catalog, please manually review."
                }
            }
        } catch (error) {
            if (error.response.status === 404) {
                const newRetailerProduct = cloneDeep(productToApprove);
                if (merchantSku) newRetailerProduct.Attributes.sku = merchantSku;
                if (merchantParentSku) newRetailerProduct.Attributes.parent_sku = merchantParentSku;
                await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Products`, newRetailerProduct);
            } else {
                let errorMessage = error.message;
                if (error.response != null) errorMessage = error.response.data.Message;
                return {
                    success: false,
                    data: null,
                    error: errorMessage
                }
            }
        }

        // UPDATE SUPPLIER PRODUCT //
        productToApprove.Attributes.sku = productId;
        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`, productToApprove);

        return {
            success: true,
            data: null,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}
export const rejectProduct = async (productId, supplierId, supplierName, rejectMessage, product) => {
    let encodedProductId = encodeURIComponent(productId);
    try {
        let productToReject = product;
        if (productToReject == null) {
            let productResponse = await axios.get(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`);
            productToReject = productResponse.data;
        }

        productToReject.ErrorMessage = rejectMessage;
        productToReject.Attributes.approval_status = "Not Compliant"

        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`, productToReject)
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error rejecting supplier product!",
            errorMessage: errorMessage
        };
    }
}
//endregion

//region RetailerCatalog API Calls
//endregion

//region General API Calls
export const utilTriggerWorkflowForPartner = async (partnerId) => {
    try {
        const response = await axios.get(Config.api + `/odata/company/Functions.RequestCatalogSync?partnerCoId=${partnerId}`);
        return {
            success: true,
            data: response.data,
            error: null
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilGetPartnerSuspensions = async () => {
    try {
        const response = await axios.get(Config.api + `/api/v2/Partners/Suspensions`);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting partner suspensions!",
            errorMessage: errorMessage
        };
    }
}

export const utilCreatePartnerSuspension = async (partnerId, reasonCode, message, startDate, endDate) => {
    try {
        const body = {
            PartnerId: partnerId,
            ReasonCode: reasonCode,
            Message: message,
            StartDate: startDate,
            EndDate: endDate
        }

        await axios.post(Config.api + '/api/v2/Partners/Suspensions', body);
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating partner suspension!",
            errorMessage: errorMessage
        };
    }
}

export const utilDeletePartnerSuspension = async (partnerId) => {
    try {
        await axios.delete(Config.api + `/api/v2/Partners/Suspensions/${partnerId}`);
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting partner suspension!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetOrderStatusCounts = async (isRetailer, partnerId, startDate, endDate) => {
    try {
        const response = await axios.get(Config.api + `/odata/Company/Functions.GetOrderStatusCounts?$orderby=Date desc&isMerchant=${isRetailer}&partnerId=${partnerId}&startDate=${startDate}&endDate=${endDate}&timeZoneOffset=${new Date().getTimezoneOffset()}`);
        return response.data.value;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting order status counts!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetPartnerScorecards = async (startDate, endDate, includeBlanks) => {
    try {
        const response = await axios.get(Config.api + `/api/v2/Partners/Scorecards?startDate=${startDate}&endDate=${endDate}&includeBlank=${includeBlanks}`);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting partner scorecards!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetPartnerSettings = async (partnerId) => {
    try {
        let url = Config.api + `/odata/Company/Functions.GetInventorySettings`;
        if (partnerId) {
            url += `?partnerId=${partnerId}`;
        }

        let configProperties = (await axios.get(url));
        let configData = configProperties.data;

        let settings = {
            autoMatch: configData[Properties.AutoMatch] === 'true',
            ignoreUnmatched: configData[Properties.IgnoreUnmatched] === 'true',
            caseSensitive: configData[Properties.CaseSensitive] === 'true',
            latestFeedOnly: configData[Properties.LatestFeedOnly] === 'true',
            allocation: 100,
            clearAfter: null,
            vendorNumber: '',
            forceVendorNumber: configData[Properties.ForceVendorNumber] === 'true',
        };

        let configuredAllocation = configData[Properties.Allocation];
        if (configuredAllocation) {
            settings.allocation = parseInt(parseFloat(configuredAllocation) * 100);
        }

        let clearAfter = configData[Properties.ClearAfter];
        if (clearAfter) {
            settings.clearAfter = clearAfter;
        }

        let vendorNumber = configData[Properties.VendorNumber];
        if (vendorNumber) {
            settings.vendorNumber = vendorNumber;
        }

        let minQty = configData[Properties.MinimumQuantity];
        if (minQty) {
            settings.minQty = parseInt(minQty);
        }

        return settings;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting partner settings!",
            errorMessage: errorMessage
        };
    }
}

//#region Inventory Management API Calls
export const utilSearchInventory = async (skus) => {
    const url = Config.api + "/odata/Company/Functions.ItemSearch"
        + `?$filter=${skus.map(sku => `(supplierSku eq '${encodeURIComponent((sku || "").replaceAll("'", "''"))}')`).join(" or ")}`;

    try {
        const response = await axios.get(url);
        return {
            data: response.data.value,
            error: null,
            success: true
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: null,
            error: errorMessage,
            success: false
        };
    }
}

export const utilSearchSupplierCatalog = async (skus) => {
    const url = Config.api + "/odata/Company/Functions.SupplierCatalogSearch"
        + `?$filter=${skus.map(sku => `(sku eq '${encodeURIComponent(sku)}')`).join(" or ")}`;

    try {
        const response = await axios.get(url);
        return {
            data: response.data.value,
            error: null,
            success: true
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: null,
            error: errorMessage,
            success: false
        };
    }
}

export const utilCheckInventory = async (partnerId, sku) => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/item?sku=${sku}`;
    try {
        const response = await axios.get(url);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error checking inventory!",
            errorMessage: errorMessage,
            errorStatus: error.response.status
        };
    }
}

export const utilCheckInventory2 = async (partnerId, sku) => {
    const url = Config.api + `/api/v2/inventory/${partnerId}/item?sku=${sku}`;
    try {
        const response = await axios.get(url);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error checking inventory!",
            errorMessage: errorMessage,
            errorStatus: error.response.status
        };
    }
}

export const utilDeleteAllInventory = async (partnerId) => {
    const url = Config.api + `/api/v2/inventory/${partnerId}`;
    try {
        await axios.delete(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilClearSkuMatches = async (partnerId) => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/matching`;
    try {
        await axios.delete(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error clearing SKU matches!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetInventorySummary = async (partnerId) => {
    const url = Config.api + `/odata/Company/Functions.InventorySummary?partnerId=${partnerId}`;
    try {
        const response = await axios.get(url);
        const summary = response.data;
        let lastUpdate = '';
        if (summary.LastUpdate) {
            lastUpdate = moment(summary.LastUpdate).format("MM/DD/YYYY hh:mm:ss A");
        }
        return {
            lastUpdate: lastUpdate || ' ',
            lastUpdateCount: summary.LastUpdateCount === null ? ' ' : summary.LastUpdateCount
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting inventory summary!",
            errorMessage: errorMessage
        };
    }
}

export const utilZeroOutAllInventory = async (partnerId) => {
    const url = Config.api + `/api/v2/inventory/${partnerId}/ZeroOut`;
    try {
        await axios.put(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error zeroing out inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilResendInventory = async (partnerId) => {
    const url = Config.api + `/api/v2/inventory/${partnerId}/resend`;
    try {
        await axios.put(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error resending inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilDeleteInventory = async (partnerId, sku) => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/item?sku=${sku}`;
    try {
        await axios.delete(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilAddInventory = async (partnerId, merchantSku, supplierSku, existingInventory) => {
    let values = {
        "SupplierId": partnerId,
        "SupplierSKU": supplierSku,
        "MerchantSKU": merchantSku,
    };

    if (existingInventory) {
        values = {...existingInventory, ...values}
    } else {
        values = {...values, "new": true}
    }

    const url = Config.api + `/api/v1/inventory/${partnerId}/item`;
    try {
        await axios.post(url, values);
        await new Promise(resolve => setTimeout(resolve, 750));
        return true;
    } catch (e) {
        var errors = ["Unexpected error occurred."];
        if (e.response) {
            errors = new Helper().getApiErrors(e.response.data);
        }
        return {
            open: true,
            errorHeader: "Error adding inventory!",
            errorMessage: errors.join(", ")
        };
    }
}

export const utilUpdateInventory = async (partnerId, inventory) => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/item`;
    try {
        await axios.put(url, inventory);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilExportInventory = async (partnerId, format, matched, transform, modifiedAfter, filter) => {
    const requestUrl = partnerId === "all" 
        ? Config.api + '/api/v2/Inventory/All/Export'
        : Config.api + `/api/v2/Inventory/${partnerId}/Export`;
    const payload = {
        Mapped: matched,
        Transform: transform,
        IncludeNullQuantity: true,
        FileType: format
    };

    if (modifiedAfter) {
        payload.ModifiedAfter = modifiedAfter;
    }

    if (filter) {
        payload.Filter = filter;
    }

    try {
        let response = await axios.post(requestUrl, payload);
        let url = response.data.Body;
        let suffix = matched ? "mapped" : "unmapped";
        return {
            href: url,
            fileName: moment().format('YYYYMMDDHHmmss') + "-" + suffix + "." + format
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error exporting inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilImportInventory = async (partnerId, file, transform = false, fileType = 'standard', bypassValidation = false) => {
    let url = "";
    if(fileType === 'standard' || fileType === 'inventoryFeed'){
        url = Config.api + `/api/v2/inventory/${partnerId}?transform=false`;
    }else if(fileType === 'supplier' || fileType === 'customFeed'){
        url = Config.api + `/api/v2/inventory/${partnerId}`;
    }else if(fileType === 'matched' || fileType === 'matchedSkuFeed'){
        url = Config.api + `/api/v2/inventory/${partnerId}/matching`;
    }
    
    const formData = new FormData();
    formData.append("file", file);

    try {
        if (!bypassValidation) {
            let validationResult = await validateFlatFile(file, 'Inventory');
            if (validationResult) {
                return {
                open: true,
                errorHeader: "Validation Warning",
                errorMessage: validationResult
                };
            }
        }

        await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error importing inventory!",
            errorMessage: errorMessage
        };
    }
}
//#endregion

export const updatePostStatus = async (postId, status) => {
    try {
        await axios.post(Config.api + '/odata/Company/Functions.UpdatePostStatus', {ThreadId: postId, Status: status});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error updating post status!",
            errorMessage: errorMessage
        };
    }
}
export const getThreadDetails = async (threadId) => {
    try {
        let response = await axios.get(Config.api + `/odata/Company/Functions.GetThreadDetails?threadId=${threadId}`);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting thread details!",
            errorMessage: errorMessage
        };
    }

}
export const getPosts = async (reference, category, partner, coid, isRetailer) => {
    const filter = `(Category eq '${encodeURIComponent(category)}' and Reference eq '${encodeURIComponent(reference)}')`;

    try {
        return (await axios.get(Config.api + `/odata/Company/VwCompanyPosts?$filter=${filter}&$orderby=Date desc`)).data.value;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting message posts!",
            errorMessage: errorMessage
        };
    }
}
export const getPostThreads = async (threadId, reference, partner, coid, isRetailer) => {
    // const contactCheck = isRetailer ? "" : `and Sender eq ${partner}`
    const filter = `(ThreadId eq ${threadId} and Reference eq '${reference}')`;

    try {
        let threads = (await axios.get(Config.api + `/odata/Company/VwCompanyPosts?$filter=${filter}&$orderby=Date desc`)).data.value;
        return threads;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting message threads!",
            errorMessage: errorMessage
        };
    }

}
export const createPost = async (requireAck, body, subject, status, reference, category, partner) => {
    try {
        await axios.post(Config.api + '/odata/Company/Functions.NewThread', {
            Body: body,
            Subject: subject,
            Reference: reference,
            Category: category,
            Recipients: [partner],
            RequireAck: requireAck,
            Status: status,
        });

        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error creating message post!",
            errorMessage: errorMessage
        };
    }
}
export const replyPost = async (replyTo, threadId, category, body, requireAck) => {
    try {
        await axios.post(Config.api + '/odata/Company/Functions.NewPost', {
            ReplyTo: replyTo,
            ThreadId: threadId,
            Category: category,
            Body: body,
            RequireAck: requireAck
        });
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error creating message post reply!",
            errorMessage: errorMessage
        };
    }
}

export const getRetailerCategoryTemplate = async (partnerId, category) => {
    try {
        let request = {
            PartnerCoId: partnerId,
            Categories: category || []
        }
        const res = (await axios({
            url: Config.api + "/odata/Company/Functions.ProductTemplate",
            method: 'POST',
            responseType: 'blob',
            data: request
        })).data;
        const blob = new Blob([res], {type: "application/octet-stream"});
        FileSaver.saveAs(blob, `product_template.xlsx`);
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting product template!",
            errorMessage: errorMessage
        };
    }
}
export const getAllTags = async (isPartner, isRetailer) => {
    let tags = [];
    let url = "";

    if (isRetailer) {
        url = retailerCatalogSearch;
        if (isPartner) url = supplierCatalogSearch;
    } else {
        url = supplierCatalogSearch;
    }

    url = url + `?$top=0&$count=true&facets=['tags,count:50']`;

    try {
        let response = await axios.get(url);
        tags = response.data["facets"]["tags"].map((value) => value.value);
        return tags;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting tags!",
            errorMessage: errorMessage
        };
    }
}
export const getAllCategories = async (partnerId, isRetailer) => {
    try {
        let url = "";
        if (isRetailer) {
            url = partnerId
                ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Categories`
                : Config.api + `/api/v2/ProductOnboardingCenter/Categories`;
        } else {
            url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Categories`;
        }

        const response = await axios.get(url);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting categories!",
            errorMessage: errorMessage
        };
    }
}
export const getProductVariations = async (parentSku, partnerId, isPartner, isRetailer) => {
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    let searchUrl = partnerId != null ? supplierCatalogSearch : retailerCatalogSearch;
    searchUrl = searchUrl + `?$filter=parentSku eq '${parentSku}'`;

    try {
        let errors = [];
        let response = await axios.get(searchUrl);
        let values = response.data.value;
        let variations = await Promise.all(
            values.map(async (item) => {
                let productId = partnerId != null ? item.id.splice(14) : item.id.splice(7);
                try {
                    return await axios.get(url + `/${productId}`);
                } catch (error) {
                    let errorMessage = error.message;
                    if (error.response != null) errorMessage = error.response.data.Message;
                    errors.push({
                        open: true,
                        errorHeader: "Error getting product variations!",
                        errorMessage: errorMessage
                    });
                }
            }),
        );

        return {
            variants: variations,
            errors: errors
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting product variations!",
            errorMessage: errorMessage
        };
    }
}
export const putProductStatus = async (productId, supplierId, status, isSupplier, updateMessage) => {
    let encodedProductId = encodeURIComponent(productId);
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    if (isSupplier)
        url = Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts`;

    try {
        let getResponse = await axios.get(url + `?id=${encodedProductId}`);
        let product = getResponse.data;

        product.Attributes.public_note = updateMessage;
        if (isSupplier)
            product.Attributes.approval_status = status;
        else {
            product.Attributes.sync_status = status;
            if (status === "Delist Pending") {
                product.Attributes.delisted_date = moment.utc().toISOString(true);
                product.Attributes.delisted_by = Auth.getUsername();
            } else if (status === "Listing Pending") {
                product.Attributes.listed_date = moment.utc().toISOString(true);
                product.Attributes.listed_by = Auth.getUsername();
            }
        }

        await axios.put(url + `?id=${encodedProductId}`, product);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating product status!",
            errorMessage: errorMessage
        };
    }
}
export const putProductSyncStatus = async (productId, partnerId, status, isSupplier) => {
    let encodedProductId = encodeURIComponent(productId);
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    if (isSupplier)
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;

    try {
        let getResponse = await axios.get(url + `?id=${encodedProductId}`);
        let product = getResponse.data;

        product.Attributes.sync_status = status;
        if (status === "Delist Pending") {
            product.Attributes.delisted_date = moment.utc().toISOString(true);
            product.Attributes.delisted_by = Auth.getUsername();
        } else if (status === "Listing Pending") {
            product.Attributes.listed_date = moment.utc().toISOString(true);
            product.Attributes.listed_by = Auth.getUsername();
        }

        await axios.put(url + `?id=${encodedProductId}`, product);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating product sync status!",
            errorMessage: errorMessage
        };
    }
}
export const createProduct = async (product, partnerId, isRetailer) => {
    let url = "";
    if (isRetailer) {
        url = partnerId
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    try {
        let response = await axios.post(url, {...product});
        return response.data.Id;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating product!",
            errorMessage: errorMessage
        };
    }
}
export const deleteProduct = async (productId, partnerId, isPartner, isRetailer) => {
    let encodedProductId = encodeURIComponent(productId);
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    try {
        await axios.delete(url + `?id=${encodedProductId}`)
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting products!",
            errorMessage: errorMessage
        };
    }
}
export const updateProduct = async (product, productId, partnerId, isPartner, isRetailer) => {
    let encodedProductId = encodeURIComponent(productId);
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    product.ErrorMessage = null;

    try {
        let response = await axios.put(url + `?id=${encodedProductId}`, {...product});
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating product!",
            errorMessage: errorMessage
        };
    }
}
export const getProduct = async (productId, partnerId, isPartner, isRetailer) => {
    let encodedProductId = encodeURIComponent(productId);
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = isPartner
            ? url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Products`
            : url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    try {
        let response = await axios.get(url + `?id=${encodedProductId}`);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting product!",
            errorMessage: errorMessage,
            errorStatus: error.response != null ? error.response.status : 500
        };
    }
}
export const utilSearchRetailerCatalog = async (queryParams) => {
    const url = Config.api + "/odata/Company/Functions.RetailerCatalogSearch?" + queryParams;
    try {
        let response = await axios.get(url);
        return {
            data: response.data.value,
            code: response.status
        }
    } catch (error) {

        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: [],
            code: error?.response?.status ?? "500",
            error: errorMessage
        };
    }
}
//endregion

//region Settings API Calls
export const getCatalogSyncHistories = async (cancelSource) => {
    try {
        const config = cancelSource ? {cancelToken: cancelSource.token} : {};
        let url = Config.api + `/odata/company/CatalogSyncHistories?$expand=LastEvent`;
        let response = await axios.get(url, config);

        return {
            data: response.data.value,
            code: response.status
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: [],
            code: error?.response?.status ?? "500",
            error: errorMessage
        }
    }
}
export const utilGetCatalogEvents = async (partnerId, cancelSource) => {
    const url = Config.api + "/odata/Company/VwActivityEvents";
    const select = "?$select=EventDate,PartnerCoId,PartnerCompanyName,EventLevel,Summary,Details,Viewed,EventTypeId,Id,CustomerViewable";
    const other = "&$top=25&$count=true&$skip=0";
    const filter = `&$filter=(CustomerViewable eq true) and (EventTypeId eq 71) and (PartnerCoId eq ${partnerId})`;
    const order = "&$orderby=Id%20desc";

    const config = cancelSource ? {cancelToken: cancelSource.token} : {};

    try {
        const response = await axios.get(url + select + other + filter + order, config);

        if (response.data.value.length === 0) {
            return {
                success: true,
                data: "No events found",
                error: null
            }
        } else {
            return {
                success: true,
                data: null,
                error: null
            }
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: [],
            error: errorMessage
        }
    }
}
export const deleteAttribute = async (attributeName) => {
    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/Attributes/${attributeName}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error deleting attribute!",
            errorMessage: errorMessage
        };
    }
}

export const createAttribute = async (attribute) => {
    try {
        await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Attributes`, {...attribute});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating attribute!",
            errorMessage: errorMessage
        };
    }
}

export const updateAttribute = async (attribute, attributeId) => {
    // let encodedAttributeId = encodeURIComponent(attributeId);
    try {
        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/Attributes/${attributeId}`, {...attribute});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating attribute!",
            errorMessage: errorMessage
        };
    }
}

export const getAttributes = async (partnerId, isRetailer) => {
    // Don't need to worry about pagination, it might show currentPage and totalPages but it will always return all records.
    try {
        let url = isRetailer
            ? Config.api + `/api/v2/ProductOnboardingCenter/Attributes`
            : Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Attributes`;
        let response = await axios.get(url);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting attributes!",
            errorMessage: errorMessage
        };
    }
}

export const deleteCategory = async (categoryId) => {
    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/Categories/${categoryId}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error deleting category!",
            errorMessage: errorMessage
        };
    }
}

export const createCategory = async (category) => {
    try {
        await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Categories`, {...category});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating category!",
            errorMessage: errorMessage
        };
    }
}

export const updateCategory = async (category, categoryId) => {
    // let encodedCategoryId = encodeURIComponent(categoryId);
    try {
        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/Categories/${categoryId}`, {...category});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating category!",
            errorMessage: errorMessage
        };
    }
}

export const deleteAttributeSet = async (attributeSetName) => {
    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets/${attributeSetName}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error deleting attribute set!",
            errorMessage: errorMessage
        };
    }
}

export const createAttributeSet = async (attributeSet) => {
    try {
        await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets`, {...attributeSet});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error creating attribute set!",
            errorMessage: errorMessage
        };
    }
}

export const updateAttributeSet = async (attributeSet, attributeSetName) => {
    // let encodedAttributeSetId = encodeURIComponent(attributeSetId);
    try {
        let response = await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets/${attributeSetName}`, {...attributeSet});
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error updating attribute set!",
            errorMessage: errorMessage
        };
    }
}

export const getAttributeSets = async (isPartner, partnerId, cancelSource, isRetailer) => {
    try {
        let url = "";
        if (isRetailer) {
            url = isPartner ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/AttributeSets`
                : Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets`
        } else {
            url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/AttributeSets`;
        }

        // Don't need to worry about pagination, it might show currentPage and totalPages but it will always return all records.
        let response = cancelSource != null
            ? await axios.get(url, {cancelToken: cancelSource.token})
            : await axios.get(url);
        let attributeSets = response.data.Records;

        // Data validation //
        let formattedAttributeSets = attributeSets.map((attributeSet) => {
            if (attributeSet.Attributes == null) {
                attributeSet.Attributes = [];
            }
            if (attributeSet.Name == null) {
                attributeSet.Name = "";
            }
            if (attributeSet.FriendlyName == null) {
                attributeSet.FriendlyName = "";
            }
            return attributeSet;
        });

        return formattedAttributeSets;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting attribute sets!",
            errorMessage: errorMessage
        };
    }
}

export const utilDeleteAll = async (type) => {
    if (type !== "Categories" && type !== "Attributes" && type !== "AttributeSets") {
        return {
            open: true,
            errorHeader: "Error deleting all items!",
            errorMessage: "Invalid type" + type
        };
    }

    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/${type}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting all items!",
            errorMessage: errorMessage
        };
    }
}
//endregion

//region Export API Calls
export const utilGetPartners = async () => {
    try {
        return (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting partners!",
            errorMessage: errorMessage
        };
    }
}
export const exportProducts = async (exportProfile, supplierId) => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Exports/Products`;
    if (supplierId != null && supplierId !== "")
        url = Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/Exports/SupplierProducts`;

    try {
        let response = await axios.post(url, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting products!",
            errorMessage: errorMessage
        };
    }
}

export const importSupplierProducts = async (importProfile, partnerId) => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Imports/SupplierProducts`;

    try {
        let response = await axios.post(url, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing products!",
            errorMessage: errorMessage
        };
    }
}

export const importRetailerProducts = async (importProfile) => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Imports/Products`;

    try {
        let response = await axios.post(url, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing products!",
            errorMessage: errorMessage
        };
    }
}

export const importSupplierProductsStatus = async (partnerId, importId) => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Imports/SupplierProducts/Status?importId=${importId}`;

    try {
        let response = await axios.get(url);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting import status!",
            errorMessage: errorMessage
        };
    }
}

export const importRetailerProductsStatus = async (importId) => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Imports/Products/Status?importId=${importId}`;

    try {
        let response = await axios.get(url);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting import status!",
            errorMessage: errorMessage
        };
    }
}

export const exportAttributes = async (exportProfile) => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Exports/Attributes`, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting attributes!",
            errorMessage: errorMessage
        };
    }
}

export const importAttributes = async (importProfile) => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Imports/Attributes`, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing attributes!",
            errorMessage: errorMessage
        };
    }
}

export const exportCategories = async (exportProfile) => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Exports/Categories`, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting categories!",
            errorMessage: errorMessage
        };
    }
}

export const importCategories = async (importProfile) => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Imports/Categories`, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing categories!",
            errorMessage: errorMessage
        };
    }
}

export const exportAttributeSets = async (exportProfile) => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Exports/AttributeSets`, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting attribute sets!",
            errorMessage: errorMessage
        };
    }
}

export const importAttributeSets = async (importProfile) => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Imports/AttributeSets`, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error importing attribute sets!",
            errorMessage: errorMessage
        };
    }
}

export const scrollToGroup = id => {
    const group = document.getElementById(id)
    let offsetTop = group == null ? 0 : group.offsetTop
    window.scrollTo({
        top: offsetTop + -80,
        behavior: 'smooth'
    })
}
//endregion

//region Import API Calls
export const utilTestColumnMapping = async (records, fields, mappings) => {
    const url = Config.api + "/api/v2/Document/TestColumnMapping";
    const body = {
        Records: records,
        Fields: fields,
        Rules: mappings
    };

    try {
        let response = await axios.post(url, body);
        return {
            success: true,
            data: response.data.Records,
            error: null
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }


}

export const utilGetLastImport = async (partnerId, signal) => {
    const url = Config.api + "/api/v2/Attachments?type=product&receiverId=" + partnerId;
    try {
        let response = await axios.get(url, {
            signal: signal
        });
        return {
            success: true,
            data: response.data.Body,
            error: null
        };
    } catch (error) {
        if (error.name === 'CanceledError') {
            throw error;
        }

        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilSearchLastImport = async (partnerId, signal) => {
    const selectQuery = "";//"$select=Id,CreateDate,CompanyName,PartnerCompanyName,PartnerCoId,Description,Type,Size,BlobPath";
    const filterQuery = `$filter=(Type eq 'product' and Duplicate eq false and PartnerCoId eq ${partnerId})`;
    const otherQuery = `$top=1&$orderby=Id desc`;
    const url = Config.api + "/odata/Company/Attachments?" + selectQuery + "&" + filterQuery + "&" + otherQuery;

    try {
        let response = await axios.get(url, {
            signal: signal
        });
        return {
            success: true,
            data: response.data.value,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilSearchLastPartnerImports = async (partnerId, top = 5) => {
    const selectQuery = "$select=Id,CreateDate,CompanyName,PartnerCompanyName,PartnerCoId,Description,Type,Size,BlobPath";
    const filterQuery = `$filter=(Duplicate eq false and Type eq 'product' and PartnerCoId eq ${partnerId})`;
    const otherQuery = `$top=${top}&$skip=0&$orderby=Id desc`;
    const url = Config.api + "/odata/Company/Attachments?" + selectQuery + "&" + filterQuery + "&" + otherQuery;

    try {
        let response = await axios.get(url);
        return {
            success: true,
            data: response.data.value,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilSearchLastImports = async (top = 10, signal) => {
    const selectQuery = "";//"$select=Id,CreateDate,CompanyName,PartnerCompanyName,PartnerCoId,Description,Type,Size,BlobPath";
    const filterQuery = `$filter=(Type eq 'product' and Duplicate eq false)`;
    const otherQuery = `$top=${top}&$orderby=Id desc`;
    const url = Config.api + "/odata/Company/Attachments?" + selectQuery + "&" + filterQuery + "&" + otherQuery;

    try {
        let response = await axios.get(url, { signal });
        return {
            success: true,
            data: response.data.value,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}
//endregion

//region Attachments API Calls
export const createProductBackupCsv = async (selectedProducts, selectedAttributes, isRetailer, currentCatalog) => {
    try {
        const backupData = [];
        const isEditingSupplierCatalog = (currentCatalog === 'partnercatalog' && isRetailer) || 
                                         (currentCatalog !== 'partnercatalog' && !isRetailer);
        
        const headers = isEditingSupplierCatalog
            ? ['SKU', 'Supplier ID', ...selectedAttributes.map(attr => attr.FriendlyName || attr.Name)]
            : ['SKU', ...selectedAttributes.map(attr => attr.FriendlyName || attr.Name)];
        
        backupData.push(headers);
        
        for (const product of selectedProducts) {
            try {
                const partnerId = isRetailer ? product.supplierId : product.merchantId;
                const isPartner = currentCatalog === "partnercatalog";
                
                const fullProductData = await getProduct(product.sku, partnerId, isPartner, isRetailer);
                
                if (!fullProductData.errorMessage) {
                    const row = [product.sku];
                    
                    if (isEditingSupplierCatalog) {
                        const supplierIdValue = isRetailer ? (product.supplierId || '') : (partnerId || '');
                        row.push(supplierIdValue);
                    }
                    
                    selectedAttributes.forEach(attribute => {
                        const currentValue = fullProductData.Attributes?.[attribute.Name];
                        let formattedValue = '';
                        
                        if (currentValue !== null && currentValue !== undefined) {
                            if (Array.isArray(currentValue)) {
                                formattedValue = currentValue.join(', ');
                            } else if (typeof currentValue === 'boolean') {
                                formattedValue = currentValue ? 'Yes' : 'No';
                            } else {
                                formattedValue = String(currentValue);
                            }
                        }
                        
                        row.push(formattedValue);
                    });
                    
                    backupData.push(row);
                }
            } catch (error) {
                console.error(`Error fetching data for ${product.sku}:`, error);

                // Add row with SKU and empty values for failed products
                const partnerId = isRetailer ? product.supplierId : product.merchantId;
                const supplierIdValue = isRetailer ? (product.supplierId || 'ERROR') : (partnerId || 'ERROR');
                const row = isEditingSupplierCatalog
                    ? [product.sku, supplierIdValue, ...selectedAttributes.map(() => 'ERROR_FETCHING_DATA')]
                    : [product.sku, ...selectedAttributes.map(() => 'ERROR_FETCHING_DATA')];
                backupData.push(row);
            }
        }
        
        const csvContent = backupData.map(row => 
            row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma, quote, or newline
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return '"' + cellStr.replace(/"/g, '""') + '"';
                }
                return cellStr;
            }).join(',')
        ).join('\n');
        
        return csvContent;
    } catch (error) {
        console.error('Error creating backup CSV:', error);
        throw error;
    }
};

export const uploadBackupToAttachments = async (csvContent, filename, isRetailer, currentCatalog) => {
    try {
        const blob = new Blob([csvContent], { type: 'text/csv' });
        
        const formData = new FormData();
        formData.append('file', blob, filename);
        
        const type = 'poc_product_backup.csv';
        let catalogType;
        if (currentCatalog === 'partnercatalog') {
            catalogType = isRetailer ? 'Supplier Catalog' : 'Retailer Catalog';
        } else {
            catalogType = isRetailer ? 'Retailer Catalog' : 'Supplier Catalog';
        }
        const description = `${catalogType} Product Backup for Bulk Update`;
        
        const response = await axios.post(Config.api + `/api/v2/Attachments?Type=${encodeURIComponent(type)}&Description=${encodeURIComponent(description)}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        
        return {
            success: true,
            data: response.data,
            filename: filename
        };
    } catch (error) {
        console.error('Error uploading backup to attachments:', error);
        let errorMessage = error.message;
        if (error.response?.data?.Message) {
            errorMessage = error.response.data.Message;
        }
        return {
            success: false,
            error: errorMessage
        };
    }
};

export const createAndUploadProductBackup = async (selectedProducts, selectedAttributes, isRetailer, currentCatalog) => {
    try {
        const csvContent = await createProductBackupCsv(selectedProducts, selectedAttributes, isRetailer, currentCatalog);
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        let catalogType;
        if (currentCatalog === 'partnercatalog') {
            catalogType = isRetailer ? 'supplier' : 'retailer';
        } else {
            catalogType = isRetailer ? 'retailer' : 'supplier';
        }
        const filename = `product_backup_${catalogType}_${timestamp}.csv`;
        
        const uploadResult = await uploadBackupToAttachments(csvContent, filename, isRetailer, currentCatalog);
        
        if (uploadResult.success) {
            return {
                success: true,
                data: uploadResult.data,
                filename: uploadResult.filename,
                message: `Backup created successfully: ${uploadResult.filename}`
            };
        } else {
            return {
                success: false,
                error: uploadResult.error
            };
        }
    } catch (error) {
        console.error('Error creating and uploading product backup:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
//endregion
